import { AirwallexEnvironmentResponse, AirwallexIntent, AirwallexRecipientValidationResult } from '@40seas/entities/airwallex';
import { AllianzCompany, CreateAllianzCoverRequest } from '@40seas/entities/allianz';
import { Attachment, AttachmentType, CreateAttachmentDto, EntityType } from '@40seas/entities/attachments';
import { BankAccount } from '@40seas/entities/bank-account';
import { UpdateBusinessVerificationDto } from '@40seas/entities/business-verification';
import { Business, BusinessContact, CreateMissingContactDto, UpdateBusinessDto } from '@40seas/entities/businesses';
import {
    Buyer,
    BuyerOwnership,
    BuyerOwnershipClaimResult,
    BuyerOwnershipMatch,
    CreateBuyerDto,
    UpdateBuyerDto,
} from '@40seas/entities/buyers';
import { ChargeDate, HolidaysDto } from '@40seas/entities/charges';
import { Country, EntityPointer, FileResource } from '@40seas/entities/common';
import { CreditLine } from '@40seas/entities/credit-line';
import { CreateCreditReferenceDto, CreditReference } from '@40seas/entities/credit-references';
import { CurrencyExchange } from '@40seas/entities/currency-exchange';
import { FeeDefinition } from '@40seas/entities/fees';
import { CreateFileDto, FileEntity, FileUrlAndKey, PublicFileType } from '@40seas/entities/files';
import { CreateFinancialConnectionDto, FinancialConnection } from '@40seas/entities/financial-connections';
import { UpdateHubspotCompanyDto } from '@40seas/entities/hubspot';
import {
    CreateInsuranceApplicationDto,
    InsuranceApplication,
    UpdateInsuranceApplicationDto,
    CreateInsuranceCoverDto,
    InsuranceCover,
    UpdateInsuranceCoverDto,
} from '@40seas/entities/insurance';
import { BuyerGuaranteePayload, IntentType, InvoiceIntentResult, PromotionResult } from '@40seas/entities/intents';
import { CreateInvoiceDto, Invoice } from '@40seas/entities/invoices';
import { Organization } from '@40seas/entities/organizations';
import { CreatePaymentMethodDto, PaymentMethod } from '@40seas/entities/payment-methods';
import { Payout } from '@40seas/entities/payouts';
import { SearchBusinessQuery, SearchPlace } from '@40seas/entities/search';
import { LeadSupplierDto, SupplierRegisteredDto } from '@40seas/entities/sellers';
import { CreateStripeIntentDto } from '@40seas/entities/stripe-intents';
import { Transaction, TransactionType } from '@40seas/entities/transactions';
import { UserConsentsTypeEnum } from '@40seas/entities/user-consents';
import { UserBuyersInvite } from '@40seas/entities/users';
import { ValidationError } from '@40seas/errors';
import { datadogRumProvider, retriesProvider } from '@40seas/frontend/libs/providers';
import {
    Address,
    BusinessType,
    Currency,
    PaymentMethodStatus,
    PaymentMethodType,
    PaymentOptionWithFee,
    PaymentTermType,
    QueryResults,
} from '@40seas/models';
import { AuthProvider as AuthProviderEnum } from '@40seas/entities/auth';
import { authClient, getAuthProvider } from '@40seas/auth-client';
import baseAxios, { AxiosInstance } from 'axios';
import { IntentsAuthProvider } from './authProvider';
import { Lead } from '@40seas/entities/leads';
import { Checkout } from '@40seas/entities/checkout';

export class IntentsDataProvider {
    private readonly axios: Promise<AxiosInstance>;
    private readonly authProvider: IntentsAuthProvider;

    constructor(private readonly intentType: IntentType, apiUrl: string, authProvider: IntentsAuthProvider) {
        const axios = baseAxios.create({
            baseURL: apiUrl,
        });
        this.authProvider = authProvider;
        this.axios = authProvider.bootstrap(axios);
        this.authProvider.setInterceptors(axios);
    }

    getBillingRequestFlow(buyerId: string, financialConnectionId?: string): Promise<{ billingRequestId: string }> {
        const path = `v1/gocardless/billing-request-flow`;
        const body = !financialConnectionId ? { buyerId: buyerId } : { financialConnectionId: financialConnectionId, buyerId: buyerId };
        return this.sendPostRequest(path, body);
    }

    async init(intent?: IntentType): Promise<any> {
        if (!intent && !this.intentType) {
            throw new ValidationError('Invalid token, please contact your supplier for a new link.', 'invalid_token');
        }

        const path = `v1/intents/${intent ?? this.intentType}/init`;
        return retriesProvider.fetchWithRetry(() => this.sendPostRequest(path), 3, 500);
    }

    async addBearer(bearerToken: string) {
        this.authProvider.addBearer(await this.axios, bearerToken);
    }

    fetchPlaces(input: string): Promise<SearchPlace[]> {
        const path = `v1/search/places?input=${input}`;
        return this.sendGetRequest(path);
    }

    fetchPlace(placeId: string): Promise<Address> {
        const path = `v1/search/places/${placeId}`;
        return this.sendGetRequest(path);
    }

    plaidLinkTokenCreate(buyerId: string, country?: Country): Promise<string> {
        const path = `v1/plaid/link/token/create/${buyerId}${country ? `?country=${country}` : ''}`;
        return this.sendGetRequest(path);
    }

    plaidReconnectLinkTokenCreate(accountId: string): Promise<string> {
        const path = `v1/plaid/link/reconnect/token/create?accountId=${accountId}`;
        return this.sendGetRequest(path);
    }

    plaidPublicTokenExchange(publicToken: string, buyerId: string): Promise<PaymentMethod[]> {
        const path = `v1/plaid/link/public-token/exchange/${buyerId}`;
        return this.sendPostRequest(path, { publicToken });
    }

    refreshFinancialConnectionStatus(financialConnectionId: string): Promise<FinancialConnection> {
        const path = `v1/financial-connections/${financialConnectionId}/refresh-status`;
        return this.sendPostRequest(path);
    }

    confirmEntity(
        paymentOptionWithFee: PaymentOptionWithFee | undefined,
        paymentMethodId?: string,
        currencyExchange?: CurrencyExchange,
        amount?: number,
        currency?: Currency,
        paymentDate?: Date,
        autoConfirmConsent?: boolean,
    ): Promise<any> {
        const baseUrl = this.intentType === IntentType.Checkout ? 'checkout' : 'intents/invoice';
        const path = `v1/${baseUrl}/confirm`;

        return this.sendPostRequest(path, {
            paymentOptionWithFee,
            userAgent: navigator.userAgent,
            sessionId: datadogRumProvider.getSessionId(),
            paymentMethodId,
            currencyExchange,
            amount,
            currency,
            paymentDate,
            autoConfirmConsent,
        });
    }

    paymentAgreement(paymentOptionWithFee: PaymentOptionWithFee | undefined, id?: string): Promise<any> {
        const baseUrl = this.intentType === IntentType.Checkout ? 'checkout' : 'invoice';
        const path = `v1/intents/${baseUrl}/paymentAgreement`;
        return this.sendPostRequest(path, { paymentOptionWithFee, id });
    }

    createCreditLineForBuyer(buyer: string, requestedAmount: number, currency: Currency): Promise<void> {
        const path = `v1/creditline/requests`;
        return this.sendPostRequest(path, {
            buyer,
            requestedAmount,
            currency,
        });
    }

    getBuyer(buyerId: string): Promise<Buyer> {
        const path = `v1/buyers/${buyerId}`;
        return this.sendGetRequest(path);
    }

    getBuyerCreditLine(buyerId: string): Promise<CreditLine> {
        const path = `v1/creditline/buyer/${buyerId}`;
        return this.sendGetRequest(path);
    }

    invoiceAttachmentDownloadUrl(attachment: Attachment): Promise<{ name: string; url: string }> {
        const path = `v1/attachments/downloadUrl/${attachment.id}`;
        return this.sendGetRequest(path);
    }

    invoiceAttachmentsBulkDownloadUrl(): Promise<{ name: string; url: string }[]> {
        const path = 'v1/intents/invoice/bulkDownloadUrl';
        return this.sendGetRequest(path);
    }

    async getSupplierBankAccount(): Promise<QueryResults<BankAccount>> {
        const path = `v1/bankaccount`;
        return this.sendGetRequest(path);
    }

    async updateCompany(buyerId: string, updateHubspotCompanyDto: UpdateHubspotCompanyDto): Promise<QueryResults<BankAccount>> {
        const path = `v1/hubspot/company`;
        return this.sendPutRequest(path, {
            buyerId,
            updateHubspotCompanyDto,
        });
    }

    async createBuyerContact(buyerId: string, createMissingContactDto: CreateMissingContactDto): Promise<any> {
        const businessContactPath = `v1/buyers/${buyerId}/contacts`;
        return this.sendPostRequest(businessContactPath, createMissingContactDto);
    }

    async updateBuyerContact(buyerId: string, contactId: string, createMissingContactDto: CreateMissingContactDto): Promise<any> {
        const businessContactPath = `v1/buyers/${buyerId}/contacts/${contactId}`;
        return this.sendPutRequest(businessContactPath, createMissingContactDto);
    }

    async createOrUpdateBuyerContact(buyerId: string, createMissingContactDto: CreateMissingContactDto): Promise<any> {
        const businessContactPath = `v1/buyers/${buyerId}/contacts/createOrUpdate`;
        return this.sendPostRequest(businessContactPath, createMissingContactDto);
    }

    async createSupplierContact(createMissingContactDto: CreateMissingContactDto): Promise<any> {
        const businessContactPath = `v1/users/settings/contacts`;
        return this.sendPostRequest(businessContactPath, createMissingContactDto);
    }

    async setSupplierContactSignatory(contactId: string): Promise<any> {
        const businessContactPath = `v1/users/settings/contacts/${contactId}`;
        return this.sendPutRequest(businessContactPath, {
            isPrimary: true,
            hasSignatureRights: true,
        });
    }

    searchCompany(SearchBusinessQuery: SearchBusinessQuery): Promise<any> {
        const path = 'v1/search';
        return this.sendGetRequest(path, SearchBusinessQuery);
    }

    searchAllianzCompanyById(companyId: string): Promise<any> {
        const path = `v1/allianz/companies/find/${companyId}`;
        return this.sendGetRequest(path);
    }

    updateAllianzCompany(id: string, data): Promise<AllianzCompany> {
        const path = `v1/allianz/companies/${id}`;
        return this.sendPutRequest(path, data);
    }

    createCreditReference(data: CreateCreditReferenceDto): Promise<CreditReference> {
        const path = `v1/credit-references`;
        return this.sendPostRequest(path, data);
    }

    createInsuranceApplication(data: CreateInsuranceApplicationDto): Promise<InsuranceApplication> {
        const path = `v1/insurance/application`;
        return this.sendPostRequest(path, data);
    }

    verifyAndConnectInsurance(id: string, credentials: { apiKey: string; policyId: string }): Promise<InsuranceApplication> {
        const path = `v1/insurance/application/${id}/verify-and-connect`;
        return this.sendPostRequest(path, credentials);
    }

    updateInsuranceApplication(id: string, data: UpdateInsuranceApplicationDto): Promise<InsuranceApplication> {
        const path = `v1/insurance/application/${id}`;
        return this.sendPutRequest(path, data);
    }

    createInsuranceCover(data: CreateInsuranceCoverDto): Promise<InsuranceCover> {
        const path = `v1/insurance/covers`;
        return this.sendPostRequest(path, data);
    }

    updateInsuranceCover(id: string, data: UpdateInsuranceCoverDto): Promise<InsuranceCover> {
        const path = `v1/insurance/covers/${id}`;
        return this.sendPutRequest(path, data);
    }

    sendRequestCover(coverRequestParams: CreateAllianzCoverRequest): Promise<any> {
        const path = `v1/allianz/cover-requests`;
        return this.sendPostRequest(path, coverRequestParams);
    }

    createBuyer(data: CreateBuyerDto): Promise<Buyer> {
        const path = `v1/intents/buyer`;
        return this.sendPostRequest(path, data);
    }

    claimBuyerOwnership(): Promise<BuyerOwnership> {
        const path = `v1/buyers/ownership`;
        return this.sendPostRequest(path);
    }

    claimBuyer(buyerPointer: EntityPointer): Promise<Buyer> {
        const path = `v1/intents/buyer/claim`;
        return this.sendPostRequest(path, buyerPointer);
    }

    confirmSupplierAgreement(): Promise<Attachment> {
        const path = `v1/intents/supplier-agreement/confirm`;
        return this.sendPostRequest(path, {});
    }

    confirmPaymentProcessingAgreement(): Promise<Attachment> {
        const path = `v1/intents/payment-processing-agreement/confirm`;
        return this.sendPostRequest(path, {});
    }

    createBankAccount(data: any): Promise<BankAccount> {
        const path = `v1/bank-account`;
        return this.sendPostRequest(path, data);
    }

    validateBankAccount(data: any): Promise<string | AirwallexRecipientValidationResult> {
        const path = `v1/bank-account/validate`;
        return this.sendPostRequest(path, data);
    }

    confirmBuyerGuaranteeContact(): Promise<BusinessContact> {
        const path = `v1/intents/buyer-guarantee/contact`;
        return this.sendGetRequest(path);
    }

    sendUserBuyerInvite(userBuyersInvite: UserBuyersInvite): Promise<any> {
        const inviteOwnerPath = `v1/users/buyers/invite`;
        return this.sendPostRequest(inviteOwnerPath, userBuyersInvite);
    }

    confirmBuyerGuaranteeAgreement(contactId: string): Promise<Attachment> {
        const path = `v1/intents/buyer-guarantee/confirm`;
        return this.sendPostRequest(`${path}/${contactId}`);
    }

    generateBuyerGuaranteeAgreement(contactId: string): Promise<BuyerGuaranteePayload> {
        const path = `v1/intents/buyer-guarantee/agreement`;
        return this.sendPostRequest(`${path}/${contactId}`);
    }

    updateBuyer(buyerId: string, data: UpdateBuyerDto): Promise<Buyer> {
        const path = `v1/buyers/${buyerId}`;
        return this.sendPutRequest(path, data);
    }

    findAndClaimBuyer(createBuyerDto: CreateBuyerDto, companyId?: string): Promise<BuyerOwnershipClaimResult> {
        const path = `v1/intents/buyer/find-and-claim`;
        return this.sendPostRequest(path, { createBuyerDto, companyId });
    }

    findBuyerOwnership(match: BuyerOwnershipMatch): Promise<BuyerOwnership> {
        const path = `v1/buyers/ownership/find`;
        return this.sendPostRequest(path, match);
    }

    replicateBuyer(buyer: Buyer): Promise<Buyer> {
        const path = `v1/intents/buyer/replicate`;

        return this.sendPostRequest(path, buyer);
    }

    getExistingBuyer(): Promise<Buyer | null> {
        const path = `v1/intents/signup/existing`;

        return this.sendPostRequest(path);
    }

    getExistingOrganization(): Promise<Organization | null> {
        const path = `v1/intents/signup-invite/existing`;

        return this.sendPostRequest(path);
    }

    acceptSupplierInvitation(organizationId: string): Promise<Organization | null> {
        const path = `v1/intents/signup-invite/accept/${organizationId}`;

        return this.sendPostRequest(path);
    }

    stripeScheduledToken(createStripeSetupIntentDto: CreateStripeIntentDto): Promise<{ clientSecret: string }> {
        const path = `v1/stripe/intents/setup`;
        return this.sendPostRequest(path, createStripeSetupIntentDto);
    }

    stripeReconnect(financialConnection: FinancialConnection): Promise<{ clientSecret: string }> {
        const path = `v1/stripe/reconnect`;
        return this.sendPostRequest(path, { financialConnection });
    }

    retrieveStripePaymentMethod(
        stripePaymentMethodId: string,
        buyerId: string,
        status: PaymentMethodStatus,
        paymentMethodType?: PaymentMethodType,
    ): Promise<PaymentMethod> {
        const path = `v1/stripe/retrieve-payment-method`;
        return this.sendPostRequest(path, { stripePaymentMethodId, buyerId, status, paymentMethodType });
    }

    verificationSubmitted(email: string): Promise<void> {
        const path = `v1/identity-verification/submitted`;
        return this.sendPostRequest(path, { email });
    }

    startVerificationIdentitySession(email: string): Promise<{ clientSecret: string }> {
        const path = `v1/intents/verify-identity/session`;
        return this.sendPostRequest(path, { email });
    }

    getStripeImmediateCardToken(options?: {
        currencyExchange?: CurrencyExchange;
        amount?: number;
        currency?: Currency;
        country?: Country;
    }): Promise<{ clientSecret: string }> {
        const path = `v1/stripe/intents/card-checkout-payment`;
        return this.sendPostRequest(path, options ?? {});
    }

    getStripeImmediateStableCoinToken(options?: {
        currencyExchange?: CurrencyExchange;
        amount?: number;
        currency?: Currency;
    }): Promise<{ clientSecret: string }> {
        const path = `v1/stripe/intents/stablecoin-checkout-payment`;
        return this.sendPostRequest(path, options ?? {});
    }

    getPayOfflineVirtualBankAccount(checkout, reference, buyer, currency): Promise<any> {
        const path = `v1/intents/checkout/virtual-bank-account`;
        return this.sendPostRequest(path, { checkout, reference, buyer, currency });
    }

    confirmCheckoutPaymentIntent(): Promise<void> {
        const path = `v1/stripe/intents/confirm-checkout-payment`;
        return this.sendGetRequest(path);
    }

    createNewPayment(): Promise<PaymentMethod> {
        const path = `v1/stripe/intents/create-new-payment`;
        return this.sendGetRequest(path);
    }

    createStripePaymentMethod(paymentMethodType: PaymentMethodType): Promise<PaymentMethod> {
        const path = `v1/stripe/intents/create-payment-method`;
        return this.sendPostRequest(path, { paymentMethodType });
    }

    confirmBusiness(business: string, updateDto: UpdateBusinessVerificationDto): Promise<any> {
        const path = `v1/business-verification/confirm/${business}`;
        return this.sendPostRequest(path, updateDto);
    }

    loadOrdersHistoryTemplate(): Promise<any> {
        const path = `v1/financial-connections/orders-history/agreement`;

        return this.sendPostRequest(path);
    }

    loadCurrencyExchange(
        from,
        to,
        amount,
        businessType,
        entities?: {
            type: string;
            id: string;
        }[],
    ): Promise<CurrencyExchange> {
        const path = `v1/currency-exchanges/quote`;
        const date = new Date().toISOString();

        return this.sendPostRequest(path, { from, to, amount, date, businessType, entities });
    }

    convertWalletBalance(amount: number, quoteId: string): Promise<CurrencyExchange> {
        const path = `v1/transactions/convert-balance`;
        return this.sendPostRequest(path, { amount, quoteId });
    }

    loadGocardlessBanks(buyerId: string, country?: Country): Promise<any> {
        const path = `v1/gocardless/financial-connection/banks/${buyerId}${country ? `?country=${country}` : ''}`;

        return this.sendGetRequest(path);
    }

    createCodatCompany(business: string, businessType: BusinessType): Promise<any> {
        const path = `v1/codat/companies`;

        return this.sendPostRequest(path, { business, businessType });
    }

    rutterLink(publicToken: string, businessType: BusinessType): Promise<any> {
        const path = `v1/rutter/link`;

        return this.sendPostRequest(path, { publicToken, businessType });
    }

    rutterPublicKey(): Promise<any> {
        const path = `v1/rutter/public-key`;

        return this.sendGetRequest(path);
    }

    createGocardlessLink(institutionId: string, redirect: string): Promise<{ link: string; id: string }> {
        const path = `v1/intents/financial-connection/gocardless/link`;

        return this.sendPostRequest(path, { institutionId, redirect });
    }

    gocardlessConnectAccounts({ gocardlessRequisitionId }: { gocardlessRequisitionId: string }): Promise<any> {
        const path = `v1/intents/financial-connection/gocardless/connection`;

        return this.sendPostRequest(path, { gocardlessRequisitionId });
    }

    provideOrdersHistoryConsent(): Promise<any> {
        const path = `v1/user-consents/provide`;

        return this.sendPostRequest(path, {
            type: UserConsentsTypeEnum.OrdersHistory,
        });
    }

    uploadFile({ name }: File, resource: FileResource): Promise<FileUrlAndKey> {
        const path = `v1/files/uploadUrl`;
        return this.sendPostRequest(path, { resource, name });
    }

    uploadPublicFile({ name }: File, type: PublicFileType): Promise<{ uploadUrl: string; downloadUrl: string }> {
        const path = `v1/files-public/upload-url`;
        return this.sendPostRequest(path, { name, type });
    }

    createFile(createFileDto: CreateFileDto): Promise<FileEntity> {
        const path = `v1/files`;
        return this.sendPostRequest(path, createFileDto);
    }

    createInvoice(invoice: CreateInvoiceDto): Promise<Invoice> {
        const path = `v1/intents/buyer-invoice/create-invoice`;
        return this.sendPostRequest(path, invoice);
    }

    createAttachment(invoice: CreateAttachmentDto): Promise<Attachment> {
        const path = `v1/attachments`;
        return this.sendPostRequest(path, invoice);
    }

    async getProofOfPaymentAttachments(checkoutId: string): Promise<Attachment[]> {
        const params = new URLSearchParams();
        params.append('filter[entityId][eq]', checkoutId);
        params.append('filter[entityType][eq]', EntityType.Checkout);
        params.append('filter[type][eq]', AttachmentType.ProofOfPayment);
        const { results } = await this.sendGetRequest('v1/attachments', params);
        return results;
    }

    async confirmPaymentMethod(paymentMethodId: string): Promise<{
        hasNextAction: boolean;
        error?: string | { code: string; message: string };
    }> {
        const path = 'v1/stripe/intents/confirm-card-payment';
        return this.sendPostRequest(path, { paymentMethodId });
    }

    async confirmStablecoinPayment(): Promise<Checkout> {
        const path = 'v1/checkout/confirm-with-stablecoin';
        return this.sendPostRequest(path);
    }

    async registerSupplier(data: SupplierRegisteredDto): Promise<Organization | Lead> {
        const path = `v1/sellers/register`;
        return this.sendPostRequest(path, data);
    }

    updateSupplierDetails(updateBusinessDto: UpdateBusinessDto): Promise<Business> {
        const path = `v1/users/settings`;

        return this.sendPutRequest(path, updateBusinessDto);
    }

    updateLogo(logo: string): Promise<Business> {
        const path = `v1/users/settings/logo`;

        return this.sendPostRequest(path, { logo });
    }

    async getLead(): Promise<LeadSupplierDto> {
        const path = `v1/leads/existing`;
        return this.sendGetRequest(path);
    }

    async getFinancialConnections(): Promise<QueryResults<FinancialConnection>> {
        const path = `v1/financial-connections`;
        return this.sendGetRequest(path);
    }

    async createFinancialConnection(createFinancialConnection: CreateFinancialConnectionDto): Promise<FinancialConnection> {
        const path = `v1/financial-connections`;
        return this.sendPostRequest(path, createFinancialConnection);
    }

    async sendEmailVerification() {
        const path = `v1/users/email-verification`;
        return this.sendGetRequest(path, {}, { 'redirect-url': window.location.href });
    }

    async sendEmailVerificationCode(): Promise<void> {
        const path = `v1/users/email-verification/request`;
        return this.sendPostRequest(path);
    }

    async checkEmailVerificationCode(code: string): Promise<boolean> {
        const path = `v1/users/email-verification/check`;
        return this.sendPostRequest(path, { code });
    }

    async sendPasswordResetRequest(email: string): Promise<void> {
        if (getAuthProvider() === AuthProviderEnum.SelfManaged) {
            await authClient.requestPasswordReset(email);
            return;
        }
        const path = `v1/users/password-reset/request`;
        return this.sendPostRequest(path, { email });
    }

    async validatePasswordResetRequest(email: string, code: string): Promise<boolean> {
        if (getAuthProvider() === AuthProviderEnum.SelfManaged) {
            const result = await authClient.validatePasswordResetCode(email, code);
            return result.valid;
        }
        const path = `v1/users/password-reset/validate`;
        return this.sendPostRequest(path, { email, code });
    }

    async linkAccounts(code: string): Promise<boolean> {
        const path = `v1/users/link-accounts`;
        return this.sendPostRequest(path, {});
    }

    async performPasswordReset(email: string, code: string, password: string): Promise<boolean> {
        if (getAuthProvider() === AuthProviderEnum.SelfManaged) {
            await authClient.resetPassword(email, code, password);
            return true;
        }
        const path = `v1/users/password-reset/change`;
        return this.sendPostRequest(path, { email, code, password });
    }

    async getIntentPromotions(intentType: IntentType): Promise<PromotionResult[]> {
        const path = `v1/intents/${intentType}/promotions`;
        return this.sendGetRequest(path);
    }

    async getActivatedBuyer(buyerId: string): Promise<Buyer> {
        const path = `v1/intents/buyer/fetch-buyer`;
        return this.sendGetRequest(path, { buyerId });
    }

    async createPaymentMethod(paymentMethod: CreatePaymentMethodDto): Promise<PaymentMethod> {
        const path = 'v1/payment-methods';
        return this.sendPostRequest(path, paymentMethod);
    }

    async getInvoice(externalId: string, amount: number): Promise<Invoice> {
        const path = `v1/invoices/${encodeURIComponent(externalId)}/externalId`;
        return this.sendGetRequest(path, { amount: amount });
    }

    async createInvoiceIntent(invoice: string): Promise<InvoiceIntentResult> {
        const path = 'v1/intents/invoice/invoice-search';
        return this.sendPostRequest(path, { invoice });
    }

    async convertPayouts(quoteId: string): Promise<Payout[]> {
        const path = `v1/payouts/currency-exchange/convert`;
        return this.sendPostRequest(path, { quoteId });
    }

    async withdraw(currency: Currency, bankAccount: string, withdrawalPayoutIds?: string[]): Promise<Transaction> {
        const path = `v1/payouts/withdrawal`;

        return this.sendPostRequest(`${path}/${currency}/${bankAccount}`, !withdrawalPayoutIds?.length ? {} : withdrawalPayoutIds);
    }

    async withdrawWalletBalance(
        amount: number,
        currency: Currency,
        bankAccount: string,
        transactionType?: TransactionType,
        note?: string,
    ): Promise<Transaction> {
        const path = `v1/transactions/withdraw`;
        const type = transactionType ?? TransactionType.Withdrawal;

        return this.sendPostRequest(path, { amount, currency, bankAccount, type, note });
    }

    async createAirwallexIntent(checkout: string, currencyExchange?: CurrencyExchange): Promise<AirwallexIntent> {
        const path = `v1/airwallex/intents/checkout/${checkout}`;
        return this.sendPostRequest(path, { currencyExchange: currencyExchange });
    }

    async getAirwallexClientSecret(customerId: string): Promise<{ clientSecret: string }> {
        const path = `v1/airwallex/customers/${customerId}/client-secret`;
        return this.sendPostRequest(path);
    }

    async getAirwallexEnvironment(): Promise<AirwallexEnvironmentResponse> {
        const path = `v1/airwallex/environment`;
        return this.sendGetRequest(path);
    }

    async chargeDates(
        country: Country,
        dateString: string,
        paymentMethodType: PaymentMethodType,
        paymentTermType: PaymentTermType,
    ): Promise<ChargeDate> {
        const path = `v1/charges/charge-dates`;
        return this.sendGetRequest(`${path}`, { country, date: dateString, paymentMethodType, paymentTermType });
    }

    async getHolidays(startDate: Date, endDate: Date, country: Country): Promise<HolidaysDto> {
        const path = `v1/intents/checkout/holidays`;
        return this.sendGetRequest(`${path}`, { startDate, endDate, country });
    }

    async queryFeeDefinitions(ids: string[], type: string = 'transaction'): Promise<FeeDefinition[]> {
        const path = `v1/fees`;

        const params = new URLSearchParams();
        ids.forEach((id) => params.append('filter[id][in][]', id));
        params.append('filter[type][eq]', type);

        const { results } = await this.sendGetRequest(path, params);

        return results;
    }

    private async sendPostRequest(path: string, payload?: unknown): Promise<any> {
        return (await (await this.axios).post(path, payload)).data;
    }

    private async sendPutRequest(path: string, payload: unknown): Promise<any> {
        return (await (await this.axios).put(path, payload)).data;
    }

    private async sendGetRequest(path: string, queryParams?: object, headers?: { [key: string]: string }): Promise<any> {
        return (await (await this.axios).get(path, { params: queryParams, headers })).data;
    }
}
